import { FC, useState } from "react"
import { Calendar } from "lucide-react"
import { LoanOfficer } from "../../../../types"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import calendlyApi from "../../../../api/calendly"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

const CalendlyConfig: FC = () => {
  const dispatch = useAppDispatch()
  const [loid, setLoid] = useState<string>("")
  const [apiKey, setApiKey] = useState<string>("")
  const [schedulingUrl, setSchedulingUrl] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)

  const canSubmit = loid && apiKey && schedulingUrl

  const handleSubmit = async () => {
    setIsLoading(true)

    try {
      await calendlyApi.configure({
        loid: parseInt(loid, 10),
        api_key: apiKey,
        scheduling_url: schedulingUrl
      })

      dispatch(
        pushToast({
          msg: "Calendly configuration saved successfully",
          theme: "success",
          timeout: 3000
        })
      )

      // Reset form
      setLoid("")
      setApiKey("")
      setSchedulingUrl("")
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save Calendly configuration"
      dispatch(
        pushToast({
          msg,
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoSelect = (lo: LoanOfficer) => {
    setLoid(lo.loid ?? "")
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Calendly Configuration
            </h3>
            <p className="text-sm text-gray-500">
              Configure Calendly integration for a loan officer
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Officer:
            </label>
            <LoanOfficerAutocomplete
              selected={loid}
              label=""
              direction="vertical"
              showBrandName
              showSearchToggle
              onChange={setLoid}
              onSelectLo={handleLoSelect}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key:
            </label>
            <input
              type="text"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter Calendly API key"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheduling URL:
            </label>
            <input
              type="text"
              value={schedulingUrl}
              onChange={e => setSchedulingUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://calendly.com/username/event"
            />
          </div>
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Saving..." : "Save Configuration"}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  )
}

export default CalendlyConfig
